import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
//components
import { TextField, ButtonField, Label, CodeFields, Message } from '../../components/hook-form';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @moment
import moment from 'moment-timezone';
// utils
import { REGEXP_ID, REGEXP_ID_START, REGEXP_EMAIL } from '../../utils/regexp';
// styles
import { AuthWrapper, FormTitle, PrimaryButton, TertiaryButton, NextButton } from '../../styles';
// apis
import { verifyFindPassword, findPassword } from '../../apis';

// ----------------------------------------------------------------------

interface FormValues {
  id?: string;
  email?: string;
};

// ----------------------------------------------------------------------

const schema = yup.object().shape({
  id: yup.string()
    .required()
    .matches(REGEXP_ID)
    .matches(REGEXP_ID_START),
  email: yup.string()
    .required()
    .trim()
    .matches(REGEXP_EMAIL, {message: '이메일 형식을 확인해 주세요.'})
});

// ----------------------------------------------------------------------

export default function FindPasswordPage() {
  const navigate  = useNavigate();
  const pathname  = useLocation().pathname;

  const {
    control,
    getValues,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: '',
      email: ''
    }
  });

  const [ start, setStart ] = useState(null);
  const [ remain, setRemain ] = useState(0);
  const [ message, setMessage ] = useState(null);

  const [ requestId, setRequestId ] = useState(null);
  const [ code, setCode ] = useState('');

  const minutes = String( Math.floor(remain / 60) ).padStart(2, '0');
  const seconds = String(remain % 60).padStart(2, '0');

  useEffect(() => {
    const timer = setInterval(() => {
      setRemain((current) => current - 1);
    }, 1000);

    if ( remain <= 0 ) {
      clearInterval(timer);
      setStart(null);
      if ( start ) setMessage('유효 시간이 만료되었습니다. 인증 메일을 재전송해 주세요.');
    }

    return () => clearInterval(timer);
  }, [remain]);

  const onError = (error: any) => {
    if ( Object.keys(error).length < 2 && Object.keys(error)[0] === 'id' ) alert('입력하신 정보를 다시 확인해 주세요.');
  }

  const onSubmit = async (data: FormValues) => {
    setMessage(null);
    await verifyFindPassword(data.id, data.email)
      .then((response) => {
        const { requestId, expirationAt } = response;
        setRequestId(requestId);
        setStart(true);
        setRemain(moment(expirationAt).diff(moment().utc(), 'seconds'));
        setMessage(null);
        setCode('');
      })
      .catch((error) => alert(error.message));
  };

  const onEmail = async () => {
    const identification = getValues('id');
    const email = getValues('email');
    if ( String(code).length === 6 ) {
      setMessage(null);
      await findPassword(requestId, identification, email, code)
        .then(() => {
          navigate('/success', {state: { submit: true, type: 'verify' }});
        })
        .catch((error) => {
          const code = error.response.data.code;
          if ( code === 'ERR-034' ) return setRemain(0);
          if ( code === 'ERR-032' ) return alert('입력하신 정보를 다시 확인해 주세요.');
          if ( code === 'ERR-035' || code === 'ERR-004' ) return alert('인증 코드를 다시 확인해주세요.');
          alert('임시 비밀번호 메일을 전송할 수 없습니다. 나중에 다시 시도해 주세요.')
        });
    } else {
      setMessage('인증 코드를 입력해 주세요.');
    }
  };

  return (
    <AuthWrapper>
        <FormTitle>
          <p>비밀번호 { pathname === '/reset-password' ? '재설정' : '찾기'}</p>
          <span>
            본인 확인을 위해 인증을 진행해 주세요.<br />
            회원 정보에 등록된 아이디와 이메일을 입력하고 인증 코드를 받아<br />
            임시 비밀번호를 발급받으실 수 있습니다.
          </span>
        </FormTitle>

        <form onSubmit={ handleSubmit(onSubmit, onError) } noValidate>
          <TextField name='id' control={control} label='아이디' type='text' maxLength={30} disabled={remain > 0 ? true : false} />

          <ButtonField
            name='email'
            label='이메일'
            disabled={remain > 0 ? true : false}
            control={control}
            action={
              <TertiaryButton disabled={remain > 0 ? true : false}>인증 요청</TertiaryButton>
            }
          />

          <div>
            <Label>인증 코드</Label>
            <CodeFields $disabled={remain > 0 ? false : true}>
              <input type='number' disabled={remain > 0 ? false : true} value={code} onChange={(event) => setCode(event.target.value)} min={0} max={999999} step={1} />
              { remain > 0 &&
                <p>{ minutes }:{ seconds }</p>
              }
            </CodeFields>
            { message &&
              <Message>{ message }</Message>
            }
          </div>

          <PrimaryButton type='button' disabled={remain > 0 ? false : true} onClick={() => onEmail()} style={{'marginTop': '35px'}}>
            임시 비밀번호 발급
          </PrimaryButton>
        </form>

        <NextButton onClick={() => navigate('/', {state: { submit: true }})} style={{'marginTop': '20px'}}>메인으로<div /></NextButton>
    </AuthWrapper>
  );
}